import { X } from '@phosphor-icons/react';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { SkillCard } from './SkillCard';
import { KnowledgeBase } from 'src/interfaces/knowledge-base.interface';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Loading } from 'src/components/Loading';

type KnowledgeSearchConfig = {
  kb_id: string;
  description: string;
};

export type KnowledgeSearchSkillConfig = {
  knowledge_base_configs?: KnowledgeSearchConfig[];
};

const stubKnowledgeSearchConfig: KnowledgeSearchConfig = {
  kb_id: '',
  description: '',
};

export const KnowledgeSearchSkill = ({
  config,
  onConfigChange,
}: {
  config: KnowledgeSearchSkillConfig;
  onConfigChange?: (config: KnowledgeSearchSkillConfig) => void;
}) => {
  const { get } = useAuthedRequest();
  const [loading, setLoading] = useState(false);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [entries, setEntries] = useState<KnowledgeSearchConfig[]>(
    config.knowledge_base_configs || [stubKnowledgeSearchConfig],
  );

  useEffect(() => {
    fetchKnowledgeBases();
  }, []);

  const fetchKnowledgeBases = useCallback(async (): Promise<
    KnowledgeBase[]
  > => {
    setLoading(true);
    try {
      const data = await get(`/knowledge-bases`);
      setKnowledgeBases(data.items);
      return data.items;
    } catch (error) {
      console.error('Failed to fetch knowledge bases:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [get]);

  const filterKnowledgeBases = useCallback(
    async (search: string) => {
      return knowledgeBases
        .filter((kb) => kb.label.toLowerCase().includes(search.toLowerCase()))
        .map((kb) => ({
          value: kb.id,
          label: kb.label,
        }));
    },
    [knowledgeBases],
  );

  if (loading) {
    return (
      <SkillCard>
        <Loading />
      </SkillCard>
    );
  }

  return (
    <SkillCard
      description={
        <>
          Gives the agent the ability to reference knowledge from uploaded
          documents.
          <br />*<span className="text-primary">All fields are required.</span>
        </>
      }
    >
      {entries.map((entry, index) => (
        <div
          key={index}
          className={clsx(
            'flex py-4 rounded-md bg-neutral-50',
            index !== entries.length - 1 && 'mb-4',
          )}
        >
          <div className="flex-col gap-4 w-full pl-4">
            <div className="flex gap-2 items-center w-full">
              <span>Use the knowledge base:</span>
              <div className="flex-1">
                <AutoCompleteSelectBox
                  key={`kb-${index}-${entry.kb_id}`}
                  defaultValue={
                    entry.kb_id
                      ? {
                          value: entry.kb_id,
                          label:
                            knowledgeBases.find((kb) => kb.id === entry.kb_id)
                              ?.label || '',
                        }
                      : undefined
                  }
                  fetchOptions={filterKnowledgeBases}
                  onChange={(value) => {
                    const updatedEntries = entries.map((e, i) =>
                      i === index ? { ...e, kb_id: value } : e,
                    );
                    setEntries(updatedEntries);
                    onConfigChange?.({
                      knowledge_base_configs: updatedEntries,
                    });
                  }}
                  variant="outlined"
                  color="primary"
                  size="medium"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-2 items-center w-full">
              <span>To answer questions about:</span>
              <div className="flex-1">
                <Input
                  placeholder="e.g. return policies"
                  value={entry.description}
                  onChange={(evt) => {
                    const updatedEntries = entries.map((e, i) =>
                      i === index ? { ...e, description: evt.target.value } : e,
                    );
                    setEntries(updatedEntries);
                    onConfigChange?.({
                      knowledge_base_configs: updatedEntries,
                    });
                  }}
                  disabled={loading}
                  required
                />
              </div>
            </div>
          </div>
          <div className="flex items-center mx-2">
            <Button
              type="button"
              variant="text"
              size="small"
              onClick={() => {
                const updatedEntries = entries.filter((_, i) => i !== index);
                setEntries(updatedEntries);
                onConfigChange?.({ knowledge_base_configs: updatedEntries });
              }}
              className="p-2 text-neutral-400 hover:text-error"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
      <Button
        className="mt-4 ml-0"
        onClick={() => setEntries([...entries, stubKnowledgeSearchConfig])}
        variant="text"
      >
        + Add more
      </Button>
    </SkillCard>
  );
};
